import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Heart, ArrowLeft, Droplet, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';

const NotFound = () => {
  return (
    <>
      <SEOHead
        title="404 - Page Not Found | DonorsHouse"
        description="The page you're looking for doesn't exist. Return to DonorsHouse to find blood and organ donation information."
        path="/404"
      />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl w-full text-center">

          {/* Illustration */}
          <div className="flex justify-center mb-6 animate-fade-in-up">
            <img
              src="/404-illustration.jpg"
              alt="A confused heart with question marks — page not found"
              className="w-52 h-52 object-contain drop-shadow-lg"
              width="208"
              height="208"
              loading="eager"
            />
          </div>

          {/* 404 number */}
          <div className="animate-fade-in-up delay-100">
            <p className="text-8xl font-extrabold text-red-700 leading-none mb-2 tracking-tight">404</p>
          </div>

          {/* Error Message */}
          <div className="mb-10 animate-fade-in-up delay-200">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-3">
              Page Not Found
            </h1>
            <p className="text-lg text-gray-600 mb-2">
              Oops! The page you're looking for doesn't exist.
            </p>
            <p className="text-gray-500 text-sm">
              It might have been moved, deleted, or the URL might be incorrect.
            </p>
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 mb-8 animate-fade-in-up delay-300">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Where would you like to go?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/" className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors group text-left">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors flex-shrink-0">
                  <Home className="w-4 h-4 text-red-700" aria-hidden="true" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Home</div>
                  <div className="text-xs text-gray-500">Start from the beginning</div>
                </div>
              </Link>

              <Link to="/blood-requests" className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors group text-left">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors flex-shrink-0">
                  <Droplet className="w-4 h-4 text-red-700" aria-hidden="true" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Blood Requests</div>
                  <div className="text-xs text-gray-500">Find urgent requests</div>
                </div>
              </Link>

              <Link to="/donation-centers" className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors group text-left">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors flex-shrink-0">
                  <MapPin className="w-4 h-4 text-red-700" aria-hidden="true" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Donation Centers</div>
                  <div className="text-xs text-gray-500">Find a center near you</div>
                </div>
              </Link>

              <Link to="/register" className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors group text-left">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors flex-shrink-0">
                  <Heart className="w-4 h-4 text-red-700" aria-hidden="true" fill="currentColor" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Become a Donor</div>
                  <div className="text-xs text-gray-500">Register in minutes</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-400">
            <Link to="/">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-red-700 hover:bg-red-800 text-white font-bold btn-transition"
                aria-label="Return to DonorsHouse homepage"
              >
                <Home className="w-4 h-4 mr-2" aria-hidden="true" />
                Go to Homepage
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.history.back()}
              className="w-full sm:w-auto border-gray-300 text-gray-700 hover:bg-gray-50 btn-transition"
              aria-label="Go back to the previous page"
            >
              <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
              Go Back
            </Button>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-sm text-gray-400 animate-fade-in-up delay-500">
            <p>
              If you believe this is an error, please{' '}
              <a
                href="mailto:support@donorshouse.com"
                className="text-red-700 hover:text-red-800 hover:underline underline-offset-2"
              >
                contact support
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
